import {
  Box,
  Button,
  Flex,
  Typography,
  Image,
  Picture,
} from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import React from "react"
import { Helmet } from "react-helmet-async"
import { Link } from "react-router-dom"
import styled from "styled-components"

import ReviewsChart from "areas/info/reviews-chart"
import { jewlr_t } from "helpers/application"

import CreatSomethingSpecial from "../../info/create-something-special"

const SectionTitle = styled(Typography).attrs((props) => ({
  fontSize: "26px",
  fontWeight: 500,
  lineHeight: "30px",
  textAlign: "center",
  ...props,
}))`
  text-transform: capitalize;
`

const SectionSubTitle = styled(Typography).attrs((props) => ({
  fontSize: "20px",
  fontWeight: 700,
  lineHeight: "20px",
  textAlign: "center",
  ...props,
}))``

const SectionDesc = styled(Typography).attrs((props) => ({
  fontSize: 16,
  fontWeight: 300,
  letterSpacing: "-0.02em",
  lineHeight: "22px",
  textAlign: "center",
  ...props,
}))``

const StatValue = styled(Typography).attrs((props) => ({
  fontSize: { _: 32, desktop: 42 },
  fontWeight: 400,
  lineHeight: { _: "40px", desktop: "50px" },
  textAlign: "center",
  ...props,
}))`
  text-transform: uppercase;
`

const StatTitle = styled(Typography).attrs((props) => ({
  fontSize: "16px",
  fontWeight: 400,
  lineHeight: "19px",
  textAlign: "center",
  ...props,
}))``

const StatCard = ({ title, value }) => (
  <Box flex={1} px="6px">
    <StatValue>{value}</StatValue>
    <StatTitle>{title}</StatTitle>
  </Box>
)

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
}

const statData = [
  [
    {
      title: "Founded",
      value: "2009",
    },
    {
      title: "Happy Customers",
      value: "1.5M",
    },
    {
      title: "Off Retail",
      value: "$AVE",
    },
  ],
  [
    {
      title: "Styles",
      value: "6000+",
    },
    {
      title: "Visualization",
      value: "3D",
    },
    {
      title: "Pushy Salespeople",
      value: "0",
    },
  ],
  [
    {
      title: "Day Returns",
      value: "99",
    },
    {
      title: "Shipping",
      value: "FREE",
    },
    {
      title: "1-Year Warranty",
      value: "FREE",
    },
  ],
]

const Stats = () => (
  <Box>
    {statData.map((row, r) => (
      <Flex justifyContent="space-between" key={r} mt={5}>
        {row.map((item) => (
          <StatCard key={item.title} {...item} />
        ))}
      </Flex>
    ))}
  </Box>
)

const StyledButton = styled(Button).attrs((props) => ({
  ...props,
}))`
  &:hover {
    color: ${(props) => props.theme.colors.white};
  }
`
const Br = styled.br`
  display: none;
  ${(props) => props.theme.mediaQueries.tablet`
    display: block;
  `}
`

const AboutUs = () => {
  return (
    <>
      <Helmet>
        <title>About Jewlr</title>
      </Helmet>
      <Box containWide>
        <Box
          bg="#ebf3f5"
          px={{ _: 4, desktop: 0 }}
          py={{ _: 4, desktop: "42px", tablet: 5 }}
        >
          <Typography
            as="h1"
            fontSize="32px"
            fontWeight={700}
            lineHeight="36px"
            mb={1}
            textAlign="center"
          >
            About Jewlr
          </Typography>
          <Typography
            fontSize="20px"
            fontWeight={500}
            lineHeight="24px"
            textAlign="center"
          >
            This is what happens when a group of software engineers
            <Br /> team up with designers and {jewlr_t("terms.jeweler")}s.
          </Typography>
        </Box>
        <Picture
          alt="Jewlr Team Photo"
          fallback={require("images/about-us/team-photo-desktop.jpg")}
          srcSet={[
            {
              height: "360",
              media: "(max-width: 767px)",
              sizes: "100%",
              srcSet: `${require("images/about-us/team-photo-mobile.jpg")} 767w`,
              width: "767",
            },
            {
              height: "433",
              media: "(min-width: 768px) and (max-width: 1023px)",
              sizes: "100%",
              srcSet: `
              ${require("images/about-us/team-photo-tablet.jpg")} 1020w,
              ${require("images/about-us/team-photo-tablet-2x.jpg")} 2040w
              `,
              width: "1020",
            },
            {
              height: "445",
              media: "(min-width: 1024px)",
              sizes: "1920px",
              srcSet: `
              ${require("images/about-us/team-photo-desktop.jpg")} 1920w,
              ${require("images/about-us/team-photo-desktop-2x.jpg")} 3840w
              `,
              width: "1920",
            },
          ]}
        />
        <Box contain px={2} py={6}>
          <SectionTitle as="h2" mb={2}>
            Our Mission
          </SectionTitle>
          <SectionSubTitle mb={2}>
            {jewlr_t("Jewelry")} made just for you.
          </SectionSubTitle>
          <SectionDesc mb={2} mx="auto" width={{ _: "100%", desktop: "711px" }}>
            Jewlr is a new kind of {jewlr_t("jewelry")} company designed to help
            people create and purchase custom-made{" "}
            {jewlr_t("terms.personalized")} {jewlr_t("jewelry")} online,
            wherever they are, at below retail prices. We have removed all
            obstacles and risks.
          </SectionDesc>
          <SectionDesc mb={2} mx="auto" width={{ _: "100%", desktop: "711px" }}>
            Our mission is to provide an awesome online {jewlr_t("jewelry")}
            -buying experience that is fast, easy, affordable &#8211; and fun!
          </SectionDesc>
        </Box>
      </Box>
      <Box bg="#515568" color="white" containWide pb={7} pt={6} px={2}>
        <Box contain>
          <SectionTitle as="h2" mb={2}>
            Our Story
          </SectionTitle>
          <SectionSubTitle mb={2}>We knew we could do better.</SectionSubTitle>
          <SectionDesc mb={2} mx="auto" width={{ _: "100%", desktop: "880px" }}>
            We have all experienced the challenges of buying{" "}
            {jewlr_t("jewelry")}. Pushy salespeople, limited choice and high
            prices. We set out to create a truly unique online experience where
            you choose, {jewlr_t("terms.personalize")}, and{" "}
            {jewlr_t("terms.customize")} your unique item and we make it for you
            in just a few days – and at an incredible price.
          </SectionDesc>
          <Stats />
        </Box>
      </Box>
      <Box contain px={2} py={6}>
        <SectionTitle as="h2" mb={2}>
          Lowest Prices
        </SectionTitle>
        <SectionSubTitle mb={2}>Designed & Made at Jewlr.</SectionSubTitle>
        <SectionDesc mb={2} mx="auto" width={{ _: "100%", desktop: "820px" }}>
          By making the {jewlr_t("jewelry")} ourselves and selling directly to
          you online, we are able to offer the lowest prices. No inventory means
          that your {jewlr_t("jewelry")} is created just for you within a few
          days. We ship directly to you which means you receive your{" "}
          {jewlr_t("jewelry")} quickly.
        </SectionDesc>
        <Typography
          fontSize={18}
          fontWeight={700}
          letterSpacing="-0.02em"
          mb={7}
          mt={10}
          textAlign="center"
        >
          Traditional Retail
        </Typography>

        <Picture
          alt="Retail consumer model"
          fallback={require("images/about-us/trad-consumer-model.svg")}
          srcSet={[
            {
              height: "105",
              media: "(max-width: 767px)",
              sizes: "100vw",
              srcSet: `${require("images/about-us/trad-consumer-model-m.svg")} 335w`,
              width: "335",
            },
            {
              height: "145",
              media: "(min-width: 768px)",
              sizes: "860px",
              srcSet: `${require("images/about-us/trad-consumer-model.svg")} 860w`,
              width: "860",
            },
          ]}
        />
        <Typography
          fontSize={18}
          fontWeight={700}
          letterSpacing="-0.02em"
          mb={7}
          mt={10}
          textAlign="center"
        >
          Jewlr
        </Typography>
        <Picture
          alt="Jewlr consumer model"
          fallback={require("images/about-us/jewlr-consumer-model.svg")}
          srcSet={[
            {
              height: "110",
              media: "(max-width: 767px)",
              sizes: "100vw",
              srcSet: `${require("images/about-us/jewlr-consumer-model-m.svg")} 369w`,
              width: "369",
            },
            {
              height: "145",
              media: "(min-width: 768px)",
              sizes: "872px",
              srcSet: `${require("images/about-us/jewlr-consumer-model.svg")} 872w`,
              width: "872",
            },
          ]}
        />
      </Box>
      <Box bg="grey.5" containWide px={2} py={6}>
        <Box contain>
          <SectionTitle as="h2" mb={2}>
            Made in North America
          </SectionTitle>
          <SectionSubTitle mb={2}>
            Quality made. Ethically sourced.
          </SectionSubTitle>
          <SectionDesc mb={2} mx="auto" width={{ _: "100%", desktop: "711px" }}>
            Your {jewlr_t("jewelry")} is custom-made in our own facilities in
            North America, which means we control the quality and guarantee that
            materials are ethically sourced. Our team of {jewlr_t("jewelry")}{" "}
            designers and craftsmen create your one-of-a-kind{" "}
            {jewlr_t("jewelry")} using your choice of solid precious metals and
            genuine or simulated stones.
          </SectionDesc>
          <Flex justifyContent="center" m="auto">
            <Image
              alt="US and Canada Flags"
              height="124"
              src={require("images/about-us/us-can.png")}
              width="180"
            />
          </Flex>
          <Typography
            fontSize="14px"
            letterSpacing="-0.02em"
            lineHeight="22px"
            textAlign="center"
          >
            MADE IN USA AND CANADA
          </Typography>
        </Box>
      </Box>
      <Box containWide px={2} py={6}>
        <Box contain>
          <SectionTitle as="h2" mb={2}>
            Reviews
          </SectionTitle>
          <SectionSubTitle mb={2}>Our Customers Love Us!</SectionSubTitle>
          <SectionDesc mb={2}>
            (but don&apos;t just take our word for it.....)
          </SectionDesc>
          <ReviewsChart />
          <Flex justifyContent="center">
            <StyledButton
              borderRadius={5}
              data-lc="about-us"
              forwardedAs={Link}
              height={44}
              mx="auto"
              py={0}
              to="/reviews"
              variant="primary"
              width={220}
            >
              Read Reviews
            </StyledButton>
          </Flex>
        </Box>
      </Box>
      <CreatSomethingSpecial />
    </>
  )
}

export default AboutUs
